import React, { useState, useEffect, useLayoutEffect, useCallback, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter';
import MapboxMap, { MapMarker } from '../components/MapboxMap/MapboxMap';
import FloatingPrimaryButton from '../components/FloatingPrimaryButton/FloatingPrimaryButton';
import SpecActionSheet from '../components/SpecActionSheet/SpecActionSheet';
import { useHeader } from '../components/Layout/MainLayout';
import { ChatIcon } from '../components/icons';
import { ShowListing } from '../components/DirectoryListings/ShowListing/ShowListing';
import SpecAlert from '../components/SpecAlert/SpecAlert';
import { useShare } from '../hooks/useShare';
import { useInfiniteShows, useVenues } from '../api/queries';
import type { PaginatedShowsResponse } from '../api';
import { getShowDateTime } from '../utils/showDateTime';
import './Home.css';

dayjs.extend(isSameOrAfter);

function Home() {
  const navigate = useNavigate();
  const [actionSheetOpen, setActionSheetOpen] = useState(false);
  const [toastMessage, setToastMessage] = useState<string | null>(null);
  const [focusedIndex, setFocusedIndex] = useState(0);
  const share = useShare(setToastMessage);
  const [isExiting, setIsExiting] = useState(false);
  const [isReady, setIsReady] = useState(false);
  const { setOnMoreClick, rightDrawerOpen, selectedDate, focusedDrawerShow } = useHeader();
  const scrollRef = useRef<HTMLDivElement>(null);
  const isScrollingRef = useRef(false);
  const scrollStartX = useRef(0);
  const hasInitializedRef = useRef(false);
  const initialScrollPositionRef = useRef<number | null>(null);
  const loadingMoreRef = useRef(false);

  // Fetch shows with infinite pagination and venues from API
  const {
    data,
    isLoading,
    fetchNextPage,
    fetchPreviousPage,
    hasNextPage,
    hasPreviousPage,
    isFetchingNextPage,
    isFetchingPreviousPage,
  } = useInfiniteShows(20);
  const { data: venues = [] } = useVenues();

  // Flatten paginated data into a single sorted array
  const shows = useMemo(() => {
    if (!data?.pages) return [];

    // Collect all shows from all pages
    const allShows = data.pages.flatMap((page: PaginatedShowsResponse) => page.shows);

    // Sort chronologically (ascending), accounting for time-of-day
    return allShows.sort((a, b) => {
      return getShowDateTime(a.date, a.time) - getShowDateTime(b.date, b.time);
    });
  }, [data?.pages]);

  // Track the initial "today" index - only calculated once after first load
  const todayIndexRef = useRef<number | null>(null);

  useEffect(() => {
    if (shows.length > 0 && todayIndexRef.current === null) {
      const today = dayjs().startOf('day');
      for (let i = 0; i < shows.length; i++) {
        if (dayjs(shows[i].date).startOf('day').isSameOrAfter(today)) {
          todayIndexRef.current = i;
          return;
        }
      }
      todayIndexRef.current = shows.length - 1;
    }
  }, [shows]);

  // Create a lookup map of venue names to venue data (photo URL and ID)
  const venueDataMap = useMemo(() => {
    const map = new Map<string, { photoUrl?: string; id: string }>();
    venues.forEach(venue => {
      map.set(venue.name, {
        photoUrl: venue.photo_url,
        id: venue.id,
      });
    });
    return map;
  }, [venues]);

  // Create markers from shows - one per unique venue
  // When drawer is open, only show markers for the selected date
  const markers: MapMarker[] = useMemo(() => {
    const venueMarkers = new Map<string, MapMarker>();

    const showsToDisplay = rightDrawerOpen && selectedDate
      ? shows.filter(show => show.date.split('T')[0] === selectedDate)
      : shows;

    showsToDisplay.forEach(show => {
      if (show.latitude && show.longitude && show.venue_name) {
        // Use venue_name as key to dedupe (one marker per venue)
        if (!venueMarkers.has(show.venue_name)) {
          const venueData = venueDataMap.get(show.venue_name);
          venueMarkers.set(show.venue_name, {
            id: show.venue_name, // Use venue name as ID for matching
            latitude: show.latitude,
            longitude: show.longitude,
            label: show.venue_name,
            imageUrl: venueData?.photoUrl,
            route: venueData?.id ? `/venues/${venueData.id}` : undefined,
          });
        }
      }
    });

    return Array.from(venueMarkers.values());
  }, [shows, rightDrawerOpen, selectedDate, venueDataMap]);

  // Get the focused show's venue name to highlight the marker
  const focusedShow = shows[focusedIndex];
  const focusedVenueName = focusedShow?.venue_name;

  // Get marker IDs for the selected date (when drawer is open)
  const fitBoundsMarkerIds = useMemo(() => {
    if (!rightDrawerOpen || !selectedDate) return undefined;
    const venueNames = new Set<string>();
    shows.forEach(show => {
      if (show.date.split('T')[0] === selectedDate && show.venue_name) {
        venueNames.add(show.venue_name);
      }
    });
    return venueNames.size > 0 ? Array.from(venueNames) : undefined;
  }, [rightDrawerOpen, selectedDate, shows]);

  // Navigate with exit animation
  const navigateWithExit = useCallback((path: string) => {
    if (isExiting) return;
    setIsExiting(true);
    setTimeout(() => navigate(path), 350);
  }, [isExiting, navigate]);

  // Set up the more button handler
  useEffect(() => {
    setOnMoreClick(() => setActionSheetOpen(true));
    return () => setOnMoreClick(undefined);
  }, [setOnMoreClick]);

  // Handle scroll to determine focused card, track scrolling state, and trigger infinite loading
  useEffect(() => {
    const scrollEl = scrollRef.current;
    if (!scrollEl) return;

    const handleScrollStart = () => {
      scrollStartX.current = scrollEl.scrollLeft;
    };

    const handleScroll = () => {
      // Mark as scrolling if moved more than 15px (higher threshold to avoid blocking taps)
      if (Math.abs(scrollEl.scrollLeft - scrollStartX.current) > 15) {
        isScrollingRef.current = true;
      }

      const scrollLeft = scrollEl.scrollLeft;
      const cardWidth = 340;
      const gap = 16;
      const cardWithGap = cardWidth + gap;

      // Calculate which card is closest to center based on scroll position
      const index = Math.round(scrollLeft / cardWithGap);
      const clampedIndex = Math.max(0, Math.min(shows.length - 1, index));

      if (clampedIndex !== focusedIndex) {
        setFocusedIndex(clampedIndex);
      }

      // Load more based on position in the shows list (fetch when within 5 shows of edge)
      const fetchThreshold = 5;

      // Approaching end of list (future shows) - fetch when 5 or fewer shows remain ahead
      const showsRemaining = shows.length - 1 - clampedIndex;
      if (showsRemaining <= fetchThreshold) {
        if (hasNextPage && !isFetchingNextPage && !loadingMoreRef.current) {
          loadingMoreRef.current = true;
          fetchNextPage().finally(() => {
            loadingMoreRef.current = false;
          });
        }
      }

      // Approaching start of list (past shows) - fetch when within first 5 shows
      if (clampedIndex <= fetchThreshold) {
        if (hasPreviousPage && !isFetchingPreviousPage && !loadingMoreRef.current) {
          // Store current scroll position relative to content
          const currentScrollLeft = scrollEl.scrollLeft;
          const currentScrollWidth = scrollEl.scrollWidth;

          loadingMoreRef.current = true;
          fetchPreviousPage().finally(() => {
            // After loading past shows, adjust scroll position to maintain view
            requestAnimationFrame(() => {
              if (scrollRef.current) {
                const newScrollWidth = scrollRef.current.scrollWidth;
                const addedWidth = newScrollWidth - currentScrollWidth;
                scrollRef.current.style.scrollBehavior = 'auto';
                scrollRef.current.scrollLeft = currentScrollLeft + addedWidth;
                scrollRef.current.style.scrollBehavior = '';
              }
              loadingMoreRef.current = false;
            });
          });
        }
      }
    };

    const handleScrollEnd = () => {
      // Reset scrolling state immediately if no actual scroll happened
      // Otherwise use a short delay to prevent accidental clicks during scroll momentum
      if (!isScrollingRef.current) {
        return;
      }
      // Use requestAnimationFrame to reset after the current event cycle
      requestAnimationFrame(() => {
        isScrollingRef.current = false;
      });
    };

    scrollEl.addEventListener('mousedown', handleScrollStart);
    scrollEl.addEventListener('touchstart', handleScrollStart, { passive: true });
    scrollEl.addEventListener('scroll', handleScroll, { passive: true });
    scrollEl.addEventListener('mouseup', handleScrollEnd);
    scrollEl.addEventListener('touchend', handleScrollEnd);

    return () => {
      scrollEl.removeEventListener('mousedown', handleScrollStart);
      scrollEl.removeEventListener('touchstart', handleScrollStart);
      scrollEl.removeEventListener('scroll', handleScroll);
      scrollEl.removeEventListener('mouseup', handleScrollEnd);
      scrollEl.removeEventListener('touchend', handleScrollEnd);
    };
  }, [focusedIndex, shows.length, rightDrawerOpen, hasNextPage, hasPreviousPage, isFetchingNextPage, isFetchingPreviousPage, fetchNextPage, fetchPreviousPage]);

  // Calculate initial scroll position when shows are available
  useLayoutEffect(() => {
    if (shows.length === 0 || hasInitializedRef.current) return;

    const cardWidth = 340;
    const gap = 16;
    const cardWithGap = cardWidth + gap;

    // Find the first show that is today or in the future
    const today = dayjs().startOf('day');
    let todayIndex = 0;
    for (let i = 0; i < shows.length; i++) {
      if (dayjs(shows[i].date).startOf('day').isSameOrAfter(today)) {
        todayIndex = i;
        break;
      }
    }

    const scrollPosition = todayIndex * cardWithGap;
    initialScrollPositionRef.current = scrollPosition;

    // Set scroll position immediately if element exists
    if (scrollRef.current) {
      // Temporarily disable smooth scrolling for instant positioning
      scrollRef.current.style.scrollBehavior = 'auto';
      scrollRef.current.scrollLeft = scrollPosition;
      // Re-enable smooth scrolling after positioning
      scrollRef.current.style.scrollBehavior = '';
    }

    setFocusedIndex(todayIndex);
    hasInitializedRef.current = true;
    setIsReady(true);
  }, [shows]);

  // Ref callback to set scroll position immediately when element mounts
  const scrollRefCallback = useCallback((node: HTMLDivElement | null) => {
    scrollRef.current = node;
    if (node && initialScrollPositionRef.current !== null) {
      // Disable smooth scrolling for instant positioning
      node.style.scrollBehavior = 'auto';
      node.scrollLeft = initialScrollPositionRef.current;
      node.style.scrollBehavior = '';
    }
  }, []);

  // Format show date for display
  const formatShowDate = (dateStr: string) => {
    // Parse date parts manually to avoid timezone issues with date-only strings
    const [year, month, day] = dateStr.split('T')[0].split('-').map(Number);
    return dayjs(new Date(year, month - 1, day)).format('ddd MMM D');
  };

  return (
    <div className={`home-page${isExiting ? ' home-page--exiting' : ''}`}>
      {/* Mapbox Map Background */}
      <MapboxMap
        center={[-95.9928, 36.1540]} // Tulsa
        zoom={12}
        style="mapbox://styles/mapbox/dark-v11"
        markers={markers}
        focusedMarkerId={isReady ? (rightDrawerOpen ? focusedDrawerShow?.venue_name : focusedVenueName) : undefined}
        fitBoundsMarkerIds={fitBoundsMarkerIds}
      />

      {/* Horizontally scrollable show cards - centered focus (hidden when right drawer is open) */}
      <div className="home-page__shows-container" style={{ display: rightDrawerOpen ? 'none' : undefined, opacity: isReady ? 1 : 0, transition: 'none' }}>
        <div className="home-page__fab-wrapper">
          <FloatingPrimaryButton
            icon={<ChatIcon size="m" tone="black" />}
            badge="Try it out!"
            style={{
              position: 'relative',
              fontWeight: 700,
              fontSize: '16px',
            }}
            onClick={() => navigate('/chat')}
          >
            Add Shows
          </FloatingPrimaryButton>
        </div>
        <div className="home-page__shows-scroll" ref={scrollRefCallback}>
          {isLoading ? (
            <div className="home-page__loading">Loading shows...</div>
          ) : shows.length === 0 ? (
            <div className="home-page__empty">No shows found</div>
          ) : (
            shows.map((show, index) => (
              <div
                key={show.id}
                className={`home-page__show-wrapper ${index === focusedIndex ? 'home-page__show-wrapper--focused' : ''}`}
              >
                <ShowListing
                  bandName={show.band_name}
                  date={formatShowDate(show.date)}
                  venue={show.venue_name || show.address || 'TBD'}
                  time={show.time}
                  imageUrl={show.band_photo_url}
                  soundcloudUrl={show.band_soundcloud_url}
                  withMembers={show.band_members}
                  onClick={() => navigateWithExit(`/shows/${show.id}`)}
                />
              </div>
            ))
          )}
        </div>
      </div>

      <SpecActionSheet
        open={actionSheetOpen}
        onClose={() => setActionSheetOpen(false)}
        title="Tulsa"
        items={[
          {
            key: 'share',
            label: 'Share',
            onClick: () => share({ title: 'Shows in Tulsa' }),
          },
          {
            key: 'copy-link',
            label: 'Copy Link',
            onClick: () => { navigator.clipboard?.writeText(window.location.href).catch(() => {}); setToastMessage('Link Copied'); },
          },
        ]}
      />
      <SpecAlert message={toastMessage} onDone={() => setToastMessage(null)} />
    </div>
  );
}

export default Home;
